import { MetricValueMode, MultiPlayerAggregate } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';

import { pageAndOrderParamsAtom, serverOrderParamsAtom } from '@/atoms/general';
import { fetchClientAtom } from '@/atoms/queries/client';
import { metricValueModeAtom, visibleTableColumnsAtom } from '@/atoms/table';
import { createEnrichedDataAtom } from '@/atoms/utils/enrichment';
import { ALL_ITEMS } from '@/consts/interface';
import { playersNormalisedAggregatesUrl, playersTotalAggregatesUrl } from '@/query/url';
import { hasEmptyInFilters } from '@/utils/api';
import { limitArray, sortFetchResultObjectArray } from '@/utils/array';
import { loadable } from '@/utils/jotai';
import { getOnDemandIncludeParams } from '@/utils/metrics';
import { addLink, flattenAndAddPrefix } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';
import { debouncedPlayerAggsFilterParamsAtom } from '../player/playerAggData';

const rawTeamSquadDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const metricValueMode = get(metricValueModeAtom);
  const includeParams = getOnDemandIncludeParams({
    metricKeys: get(visibleTableColumnsAtom),
    orderBy: get(serverOrderParamsAtom).order_by,
  });
  const queryKey = ['teamSquad', get(debouncedPlayerAggsFilterParamsAtom), metricValueMode, includeParams] as const;
  const queryFn = async ({
    queryKey: [, playerAggsFilterParams, metricValueMode, includeParams],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (hasEmptyInFilters(playerAggsFilterParams)) return [];
    const urlCreationFunction =
      metricValueMode === MetricValueMode.Per90 ? playersNormalisedAggregatesUrl : playersTotalAggregatesUrl;
    const data: MultiPlayerAggregate[] = await fetch(
      urlCreationFunction({
        limit: ALL_ITEMS,
        ...playerAggsFilterParams,
        ...includeParams,
      }),
    );
    return data.map(item => flattenAndAddPrefix(objSnakeToCamel(item), '.', false));
  };
  const enabled = get(debouncedPlayerAggsFilterParamsAtom) !== null;
  return { queryKey, queryFn, enabled };
});

const teamSquadDataWithLinksAsyncAtom = atom(async get => {
  const pageAndOrderParams = get(pageAndOrderParamsAtom);
  const teamSquadData = (await get(rawTeamSquadDataAtom)).data;

  const sortedTeamSquadToDisplay = sortFetchResultObjectArray(teamSquadData, pageAndOrderParams);
  const teamSquadPage = limitArray(sortedTeamSquadToDisplay, pageAndOrderParams);

  return teamSquadPage.map(data =>
    addLink(data, data['player.displayName'] as string, data['player.playerId'] as number, 'playerLink'),
  );
});

const unwrappedTeamSquadDataWithLinksAtom = unwrap(teamSquadDataWithLinksAsyncAtom, prev => prev || undefined);

const teamSquadAtom = createEnrichedDataAtom(unwrappedTeamSquadDataWithLinksAtom, 'player.playerId');

export const unwrappedTeamSquadCountAtom = unwrap(atom(async get => (await get(rawTeamSquadDataAtom)).data?.length));

export const teamSquadWithLoadingAtom = atom(get => {
  const enrichedData = get(teamSquadAtom);
  const rawDataLoadable = get(loadable(rawTeamSquadDataAtom));

  const isLoading =
    rawDataLoadable.state === 'loading' || (rawDataLoadable.state === 'hasData' && enrichedData === undefined);

  return {
    data: enrichedData,
    isLoading,
  };
});

export const teamSquadPlayerOptionsAtom = atom(async get => {
  const { data: squadData } = await get(rawTeamSquadDataAtom);
  return squadData
    .map(item => ({
      value: String(item['player.playerId'] as number),
      label: item['player.displayName'] as string,
    }))
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));
});

export const unwrappedTeamSquadPlayerOptionsAtom = unwrap(teamSquadPlayerOptionsAtom, prev => prev || []);
